/**
 * Shell store — the calm chart-first interface layer described in the UI design
 * plan. It sits on top of the existing milestone stores (M1–M8) and only holds
 * what the shell itself needs: which mode we are in, which sheet is open, the
 * one data field, the point last tapped, and the fix builder's draft state.
 *
 * Kept as its own store, like chartwork and routing, so the milestone slices in
 * ./index.ts stay untouched. The shell reads from those stores; it does not own
 * their state.
 */
import { create } from 'zustand';
import type { ChartFeature } from '@/types';
import {
  sanitiseDataFields,
  sanitisePrimaryPin,
  MAX_DATA_FIELDS,
  DATA_FIELDS_KEY,
  PRIMARY_PIN_KEY,
} from './dataFieldConfig';
import { loadJson, saveJson } from './persist';

/** Load the saved data-field boxes, cleaned of anything we no longer draw. */
function loadDataFields(): DataField[] {
  return sanitiseDataFields(loadJson<unknown>(DATA_FIELDS_KEY, null));
}

/** Load the saved primary pin (a field, or null to let context decide). */
function loadPrimaryPin(): DataField | null {
  return sanitisePrimaryPin(loadJson<unknown>(PRIMARY_PIN_KEY, null));
}

export type Mode =
  // Planning, Manual Plotting and Pilotage were merged into one 'Plotting' mode
  // that holds all of their tools; Live and Training are unchanged.
  | 'Plotting'
  | 'Training'
  | 'Live';

export type SheetName =
  | 'mode'
  | 'data'
  | 'vessel'
  | 'chart'
  | 'destination'
  | 'note'
  | 'route'
  | 'waypoint'
  | 'routealarms'
  | 'legxtd'
  | 'planning'
  | 'conditions'
  | 'weathertable'
  | 'ais'
  | 'forecast'
  | 'layers'
  | 'alarms'
  | 'settings'
  | 'clearing'
  | 'pilotage'
  | 'transit'
  | 'heading'
  | 'clearingdepth'
  | 'plotting'
  | 'fixbuilder'
  | 'runningfix'
  | 'derived'
  | 'cts'
  | 'tracks'
  | 'feature'
  | 'feedback'
  | 'menu'
  | 'search'
  | 'eblvrm'
  | 'pilotageplans'
  | 'pilotagecommunity'
  | 'whatshere'
  | 'diamond'
  | 'tides'
  | 'mobsearch';

/**
 * A draft observation in the fix builder: a mark tapped on the chart plus either
 * a bearing on it or a range off it. Turned into proper observation objects and
 * run through buildFix when the user computes the fix.
 */
export interface DraftObs {
  id: string;
  type: 'bearing' | 'range' | 'transit';
  /** The mark for a bearing or range; the FRONT mark for a transit. */
  markPosition: [number, number];
  markLabel: string;
  /** Degrees true, for a bearing observation. */
  bearing?: number;
  /** For a range observation, with its unit. */
  range?: number;
  unit?: 'nm' | 'cables' | 'm';
  /** For a transit: the rear mark (markPosition holds the front). */
  rearPosition?: [number, number];
  rearLabel?: string;
}

export type DataField =
  | 'sog'
  | 'cog'
  | 'heading'
  | 'pos'
  | 'xte'
  | 'brgwp'
  | 'dtwp'
  | 'eta'
  | 'gnss' | 'uke' | 'trip' | 'tide' | 'cpa';

/** Which waypoint the editor is editing: one in a route, or a standalone mark. */
export type WaypointRef =
  | { type: 'route'; routeId: string; wpId: string }
  | { type: 'mark'; markId: string };

/**
 * A charted object under a long-press, threaded into the ring so it can offer
 * object actions (details, plot transit, clearing bearing, set destination) and
 * seed each one with the object's charted position and label. null on open water.
 */
export interface RingObject {
  /** The object's charted position [lon, lat] (more precise than the press point). */
  position: [number, number];
  /** Short human title, e.g. the buoy's name or its class label. */
  label: string;
  /** S-57 object class acronym, or null when unknown. */
  objClass: string | null;
  /** The picked feature and its light siblings, for the details action. */
  feature: ChartFeature;
  siblings: ChartFeature[];
}

export interface ShellState {
  mode: Mode;
  setMode: (mode: Mode) => void;

  /** null means no sheet is open — the calm resting state. */
  activeSheet: SheetName | null;
  openSheet: (name: SheetName) => void;
  closeSheet: () => void;
  /** When the menu opens, navigate straight to this group id (e.g. 'layers'), then clear. null = open at root. */
  pendingMenuTarget: { group: string; leaf?: string } | null;
  setPendingMenuTarget: (t: { group: string; leaf?: string } | null) => void;
  /** Open the menu landed on a group, or directly on a leaf's focused control. */
  openMenuAt: (groupId: string, leafId?: string) => void;
  /** Human label of the menu's current drill location (group / leaf / control),
   *  or null when the menu is closed. Lifted for the feedback self-test tool. */
  menuLocation: string | null;
  setMenuLocation: (loc: string | null) => void;

  /** The data-field boxes shown top-right, each independently chosen. */
  dataFields: DataField[];
  setDataFieldAt: (index: number, field: DataField) => void;
  addDataField: () => void;
  removeDataField: (index: number) => void;
  /** Reorder a box: dir -1 moves it up the stack, +1 moves it down. */
  moveDataField: (index: number, dir: -1 | 1) => void;
  /**
   * Move a box from one position to another, the drag-and-drop reorder. Unlike
   * moveDataField this LIFTS AND REINSERTS rather than swapping neighbours: a
   * drag past three boxes should slide those three up, not shuffle the one you
   * dragged past into the hole you came from.
   */
  reorderDataField: (from: number, to: number) => void;
  /** The field pinned as primary (rendered large), or null to let context choose. */
  pinnedPrimary: DataField | null;
  setPinnedPrimary: (field: DataField | null) => void;
  /** Which box the data-field picker is currently editing. */
  editingFieldIndex: number | null;
  setEditingFieldIndex: (index: number | null) => void;

  /** The last point tapped on open water, for the chart-tap sheet. [lon, lat]. */
  /** A map fly-to request (centre + zoom); MapView watches it. */
  focus: { center: [number, number]; zoom: number } | null;
  setFocus: (center: [number, number], zoom: number) => void;
  /** A map fit-to-bounds request (e.g. loading a pilotage plan); MapView watches it.
   *  Carries a nonce so re-fitting to the same bounds still fires. */
  fit: { bounds: [number, number, number, number]; nonce: number } | null;
  fitBoundsTo: (bounds: [number, number, number, number]) => void;
  chartTap: [number, number] | null;
  /** ZOC band letter at the tapped point (the area's data quality), or null. */
  chartTapZoc: string | null;
  setChartTap: (p: [number, number] | null, zoc?: string | null) => void;

  /** The position of a tapped tidal diamond, for the diamond detail sheet. [lon, lat]. */
  diamondTap: [number, number] | null;
  setDiamondTap: (p: [number, number] | null) => void;

  /**
   * The open-water long-press radial ring: the geographic point pressed and the
   * screen pixel to draw it under. null when no ring is showing.
   */
  ring: { position: [number, number]; x: number; y: number; object?: RingObject | null } | null;
  openRing: (
    position: [number, number],
    x: number,
    y: number,
    object?: RingObject | null,
  ) => void;
  closeRing: () => void;

  /**
   * The track-actions popover: a long-press on the ownship boat or the
   * orientation (north-up / head-up) control opens this at the given screen
   * pixel. It offers stop / reset / save on the live recording track.
   * null when nothing is showing.
   */
  trackMenu: { x: number; y: number } | null;
  openTrackMenu: (x: number, y: number) => void;
  closeTrackMenu: () => void;

  /** The waypoint currently open in the editor sheet, or null. */
  editingWaypoint: WaypointRef | null;
  setEditingWaypoint: (w: WaypointRef | null) => void;

  /** The leg whose XTD corridor is being edited (drawn + draggable), or null. */
  editingLegXtd: { routeId: string; fromId: string } | null;
  setEditingLegXtd: (leg: { routeId: string; fromId: string } | null) => void;
  /** Hazard-based recommended XTD for the edited leg (nm), computed in MapView. */
  legXtdRecommendation: number | null;
  setLegXtdRecommendation: (nm: number | null) => void;

  /**
   * Whether a student is signed in with an active course. Training mode is
   * gated behind this. False in phase 1 by default — Training shows locked.
   */
  signedIn: boolean;
  setSignedIn: (on: boolean) => void;

  /**
   * Manual Plotting has two ways to work: fully manual (draw it yourself) or
   * assisted (enter the numbers, the app draws it). False = fully manual.
   */
  plottingAssisted: boolean;
  setPlottingAssisted: (on: boolean) => void;

  /** Transient toast message, cleared by the Toast component after a moment. */
  toast: string | null;
  showToast: (message: string | null) => void;

  /**
   * A confirm prompt: a question with an action the user can accept or dismiss.
   * Used for offers like "switch to Planning to build a route" so it's an
   * actionable choice, not just a message.
   */
  confirm: {
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
    /** Optional second action (e.g. an "Auto" alongside "Manual"), shown as an
     *  extra button. Dismiss is always available on top of these. */
    altLabel?: string;
    onAlt?: () => void;
  } | null;
  showConfirm: (c: {
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
    altLabel?: string;
    onAlt?: () => void;
  }) => void;
  clearConfirm: () => void;

  /**
   * Transit placement. `transitArmed` is the tool armed and waiting for the
   * BACK (rear) mark; once that's placed it moves to `pendingTransit` (back mark
   * captured, awaiting the FRONT mark tap). Both marks are placed by tapping
   * anywhere on the chart (snapping to a nearby charted object when close).
   */
  transitArmed: boolean;
  pendingTransit: { position: [number, number]; label: string } | null;
  /** Arm the tool: the next tap places the back mark. */
  armTransit: () => void;
  /** Capture the back mark (from a tap or a charted object); await the front. */
  armTransitPick: (first: { position: [number, number]; label: string }) => void;
  clearPendingTransit: () => void;

  /**
   * Heading / back bearing placement: arm the tool with the kind, then the next
   * tap drops the bearing on the tapped point (snapping to a nearby charted
   * object). A single tap, unlike the two-tap transit.
   */
  headingArmed: 'heading' | 'back' | null;
  armHeading: (kind: 'heading' | 'back') => void;
  clearHeadingArm: () => void;

  /**
   * Place-note mode. When on, the next chart tap drops a new note at that point
   * and opens its editor, so starting a note from the menu (where there's no
   * location yet) lets us choose WHERE it goes rather than dropping it blind.
   * A one-shot mode, escapable with cancel.
   */
  noteArmed: boolean;
  armPlaceNote: () => void;
  cancelPlaceNote: () => void;
  /** The note currently open in the note editor sheet, or null (new/none). */
  editingNoteId: string | null;
  setEditingNote: (id: string | null) => void;

  /**
   * A counter the recentre button bumps. MapView watches it and eases the chart
   * back onto the vessel. A counter (rather than a boolean) so repeated taps
   * always fire, even when nothing else changed.
   */
  recentreRequest: number;
  requestRecentre: () => void;

  /**
   * A counter the MOB readout bumps when tapped. MapView watches it and eases the
   * chart onto the active MOB last-known position, the same counter trick the
   * vessel recentre uses so repeated taps always fire even when nothing else
   * changed. It never engages the tracking lock, since the casualty mark doesn't
   * move with the boat.
   */
  mobRecentreRequest: number;
  requestMobRecentre: () => void;

  /**
   * Tracking lock: when on, the map keeps own ship centred as the GNSS position
   * updates (like a chartplotter's "centre on boat" lock). Disengaged when the
   * user pans the chart away. Composes with the north-up/head-up orientation.
   */
  tracking: boolean;
  setTracking: (on: boolean) => void;

  /**
   * "Ready anchor": when armed, a one-tap Drop-anchor button rides on the chart
   * HUD (like the MOB button) so the hook can be dropped at the exact moment it
   * hits the bottom without diving into the menu. Off by default; the
   * AnchorWatch control arms and disarms it. The drop itself reuses the anchor
   * store's dropAnchor — this flag only governs whether the HUD button shows.
   */
  anchorDropArmed: boolean;
  setAnchorDropArmed: (on: boolean) => void;

  /**
   * Place-anchor-on-the-chart mode. When on, the next chart tap puts the anchor
   * exactly where the finger lands.
   *
   * This is for the anchor that went down before anyone opened the app, which is
   * most of them. We know roughly where it is because we watched it go in off
   * the bow ten minutes ago, and by now the boat has dropped back on the rode,
   * so "drop it here" would record it a boat length or more astern of the truth.
   * Pointing at the spot is the honest answer.
   *
   * No bow offset is applied to the tapped point, unlike a drop at the boat. The
   * skipper is not saying "the bow is here", they are saying "the anchor is
   * there". A one-shot mode, escapable with cancel.
   */
  anchorPickArmed: boolean;
  armAnchorPick: () => void;
  cancelAnchorPick: () => void;

  // --- Fix builder ---
  /** Observations gathered so far for the fix under construction. */
  fixDraft: DraftObs[];
  /** True while waiting for the next chart tap to capture a mark. */
  fixArmed: boolean;
  /** A mark captured from the chart, awaiting its bearing or range. */
  pendingMark: { position: [number, number]; label: string } | null;
  /** The sheet to reopen once a mark is captured (fix builder or running fix). */
  pickReturn: SheetName;
  armFixPick: (returnSheet?: SheetName) => void;
  setPendingMark: (mark: { position: [number, number]; label: string } | null) => void;
  addFixObs: (obs: DraftObs) => void;
  removeFixObs: (id: string) => void;
  clearFixDraft: () => void;
}

export const useShellStore = create<ShellState>((set) => ({
  // The sim provides a default GNSS feed, so the app opens in Live Navigation
  // with own ship already on the chart. The other jobs are a tap away in the
  // mode picker.
  mode: 'Live',
  setMode: (mode) => set({ mode }),

  activeSheet: null,
  openSheet: (activeSheet) => set({ activeSheet }),
  closeSheet: () => set({ activeSheet: null }),
  pendingMenuTarget: null,
  setPendingMenuTarget: (pendingMenuTarget) => set({ pendingMenuTarget }),
  openMenuAt: (groupId, leafId) => set({ activeSheet: 'menu', pendingMenuTarget: { group: groupId, leaf: leafId } }),
  menuLocation: null,
  setMenuLocation: (menuLocation) => set({ menuLocation }),

  dataFields: loadDataFields(),
  setDataFieldAt: (index, field) =>
    set((s) => {
      const next = s.dataFields.slice();
      if (index >= 0 && index < next.length) next[index] = field;
      saveJson(DATA_FIELDS_KEY, next);
      return { dataFields: next };
    }),
  addDataField: () =>
    set((s) => {
      // Cap the stack so it can't swamp the chart; a full stack just no-ops.
      if (s.dataFields.length >= MAX_DATA_FIELDS) return s;
      const next = [...s.dataFields, 'cog' as DataField];
      saveJson(DATA_FIELDS_KEY, next);
      // Open the picker on the new box so it's chosen straight away.
      return { dataFields: next, editingFieldIndex: next.length - 1 };
    }),
  removeDataField: (index) =>
    set((s) => {
      if (s.dataFields.length <= 1) return s; // always keep one box
      const next = s.dataFields.filter((_, i) => i !== index);
      saveJson(DATA_FIELDS_KEY, next);
      return { dataFields: next, editingFieldIndex: null };
    }),
  moveDataField: (index, dir) =>
    set((s) => {
      const target = index + dir;
      if (index < 0 || index >= s.dataFields.length) return s;
      if (target < 0 || target >= s.dataFields.length) return s; // already at the end
      const next = s.dataFields.slice();
      [next[index], next[target]] = [next[target], next[index]];
      saveJson(DATA_FIELDS_KEY, next);
      // Keep the picker following the box the user just moved.
      return { dataFields: next, editingFieldIndex: target };
    }),
  reorderDataField: (from, to) =>
    set((s) => {
      const n = s.dataFields.length;
      if (from < 0 || from >= n) return s;
      const dest = Math.max(0, Math.min(n - 1, to));
      if (dest === from) return s;
      const next = s.dataFields.slice();
      const [moved] = next.splice(from, 1);
      next.splice(dest, 0, moved);
      saveJson(DATA_FIELDS_KEY, next);
      return { dataFields: next };
    }),
  pinnedPrimary: loadPrimaryPin(),
  setPinnedPrimary: (field) =>
    set(() => {
      saveJson(PRIMARY_PIN_KEY, field);
      return { pinnedPrimary: field };
    }),
  editingFieldIndex: null,
  setEditingFieldIndex: (editingFieldIndex) => set({ editingFieldIndex }),

  focus: null,
  setFocus: (center, zoom) => set({ focus: { center, zoom } }),
  fit: null,
  fitBoundsTo: (bounds) => set({ fit: { bounds, nonce: Date.now() } }),
  chartTap: null,
  chartTapZoc: null,
  setChartTap: (chartTap, chartTapZoc = null) => set({ chartTap, chartTapZoc }),

  diamondTap: null,
  setDiamondTap: (diamondTap) => set({ diamondTap }),

  ring: null,
  openRing: (position, x, y, object = null) =>
    set({ ring: { position, x, y, object }, activeSheet: null }),
  closeRing: () => set({ ring: null }),

  trackMenu: null,
  openTrackMenu: (x, y) => set({ trackMenu: { x, y }, ring: null, activeSheet: null }),
  closeTrackMenu: () => set({ trackMenu: null }),

  editingWaypoint: null,
  setEditingWaypoint: (editingWaypoint) => set({ editingWaypoint }),

  editingLegXtd: null,
  setEditingLegXtd: (editingLegXtd) => set({ editingLegXtd }),
  legXtdRecommendation: null,
  setLegXtdRecommendation: (legXtdRecommendation) => set({ legXtdRecommendation }),

  signedIn: false,
  setSignedIn: (signedIn) => set({ signedIn }),

  plottingAssisted: false,
  setPlottingAssisted: (plottingAssisted) => set({ plottingAssisted }),

  toast: null,
  showToast: (toast) => set({ toast }),

  confirm: null,
  showConfirm: (confirm) => set({ confirm }),
  clearConfirm: () => set({ confirm: null }),

  transitArmed: false,
  pendingTransit: null,
  armTransit: () => set({ transitArmed: true, pendingTransit: null, activeSheet: null }),
  armTransitPick: (pendingTransit) => set({ pendingTransit, transitArmed: false, activeSheet: null }),
  clearPendingTransit: () => set({ pendingTransit: null, transitArmed: false }),

  headingArmed: null,
  armHeading: (kind) => set({ headingArmed: kind, activeSheet: null }),
  clearHeadingArm: () => set({ headingArmed: null }),

  noteArmed: false,
  // Arm the tap-to-place mode and clear any sheet so the chart underneath is
  // tappable. Also clear any note being edited, so this tap starts a fresh one.
  armPlaceNote: () => set({ noteArmed: true, activeSheet: null, editingNoteId: null }),
  cancelPlaceNote: () => set({ noteArmed: false }),
  editingNoteId: null,
  setEditingNote: (editingNoteId) => set({ editingNoteId }),

  recentreRequest: 0,
  requestRecentre: () => set((s) => ({ recentreRequest: s.recentreRequest + 1 })),

  mobRecentreRequest: 0,
  requestMobRecentre: () => set((s) => ({ mobRecentreRequest: s.mobRecentreRequest + 1 })),

  tracking: false,
  setTracking: (tracking) => set({ tracking }),

  anchorDropArmed: false,
  setAnchorDropArmed: (anchorDropArmed) => set({ anchorDropArmed }),

  anchorPickArmed: false,
  // Close the sheet on arming, or the chart we are asking them to tap is under
  // it. Same reason armFixPick and armPlaceNote do it.
  armAnchorPick: () => set({ anchorPickArmed: true, activeSheet: null }),
  cancelAnchorPick: () => set({ anchorPickArmed: false }),

  fixDraft: [],
  fixArmed: false,
  pendingMark: null,
  // Close the sheet while picking, so the chart underneath is tappable, then
  pickReturn: 'fixbuilder',
  armFixPick: (returnSheet = 'fixbuilder') =>
    set({ fixArmed: true, activeSheet: null, pickReturn: returnSheet }),
  setPendingMark: (pendingMark) =>
    set((s) => ({ pendingMark, fixArmed: false, activeSheet: s.pickReturn })),
  addFixObs: (obs) => set((s) => ({ fixDraft: [...s.fixDraft, obs], pendingMark: null })),
  removeFixObs: (id) => set((s) => ({ fixDraft: s.fixDraft.filter((o) => o.id !== id) })),
  clearFixDraft: () => set({ fixDraft: [], pendingMark: null, fixArmed: false }),
}));
